import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectAppValue } from "../redux/selectors";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import {
  addStorageObject,
  editStorageObject,
} from "../../../app/state/storageObjectSlice/actions/actions";
import { ExtraAppValue } from "../../../services/api/instances/extraAppApi/types";
import { setExtraAppValue } from "../redux/slice";
import { useModalSelectors } from "../../../app/state/modalSlice/hook/use-modal-selectors";

export const useExtraAppModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const appValue = useAppSelector(selectAppValue);
  const manageExtraApp = useModalSelectors("manage_extra_app_modal");
  const { selectedId, objects, entities } = useStorageSelectors(
    "ai_agents_extra_app",
  );
  const [uri, setUri] = useState<string>("");
  const [title, setTitle] = useState<string>(t("extra_app.default_name"));
  const [tokenOn, setTokenOn] = useState<boolean>(false);

  useEffect(() => {
    if (manageExtraApp.isOpen) {
      if (manageExtraApp?.subtype === "create_extra_app") {
        setUri("");
        setTitle(t("extra_app.default_name"));
        setTokenOn(false);
      } else if (manageExtraApp?.subtype === "edit_extra_app") {
        const editObject = entities?.entities[manageExtraApp?.info?.id ?? ""];
        if (!editObject || !editObject?.value) return;
        const { uri, title, withToken } = (editObject.value as ExtraAppValue)
          .extra_app_object;
        setUri(uri);
        setTitle(editObject?.friendly_name);
        setTokenOn(withToken);
      }
    }
  }, [manageExtraApp?.subtype, manageExtraApp?.isOpen]);

  const handleSetForm = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "uri" | "title" | "token",
  ) => {
    const value = event.target.value;
    switch (field) {
      case "uri":
        setUri(value);
        return;
      case "title":
        setTitle(value);
        return;
      case "token":
        setTokenOn((event.target as HTMLInputElement).checked);
        return;
      default:
        return;
    }
  };

  const handleCreateExtraAppObject = useCallback(() => {
    dispatch(
      addStorageObject({
        name: title,
        service: "ai_agents_extra_app",
        value: {
          extra_app_object: {
            uri,
            title,
            withToken: tokenOn,
          },
        },
        objectType: "child",
        withoutCount: title === t("extra_app.default_name") ? false : true,
        currentObjectId: manageExtraApp?.info?.id ?? "",
      }),
    ).unwrap();
  }, [selectedId, uri, title, tokenOn, manageExtraApp?.info?.id]);

  const handleUpdateApp = useCallback(() => {
    const value: ExtraAppValue = {
      extra_app_object: {
        uri,
        title,
        withToken: tokenOn,
      },
    };
    dispatch(
      editStorageObject({
        id: manageExtraApp?.info?.id ?? "",
        name: title,
        value: value,
        service: "ai_agents_extra_app",
      }),
    );
    if (selectedId === manageExtraApp?.info?.id) {
      dispatch(setExtraAppValue(value));
    }
  }, [manageExtraApp?.info?.id, selectedId, uri, title, tokenOn]);

  return {
    uri,
    title,
    tokenOn,
    appValue,
    handleUpdateApp,
    handleCreateExtraAppObject,
    handleSetForm,
  };
};
